import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@monorepo-apps/ui/src/lib/utils";

import type { Priority, Rag, Status, TaskStatus } from "@/infrastructure/supabase/database.types";

const statusDot = cva("relative inline-block rounded-full", {
  variants: {
    tone: {
      open: "bg-[var(--muted-foreground)]",
      active: "bg-[var(--rag-amber)]",
      resolved: "bg-[var(--rag-green)]",
      blocked: "bg-[var(--rag-red)]",
      green: "bg-[var(--rag-green)]",
      amber: "bg-[var(--rag-amber)]",
      red: "bg-[var(--rag-red)]",
      cyan: "bg-[var(--rag-cyan)]",
      purple: "bg-[var(--rag-purple)]",
    },
    size: {
      sm: "size-1.5",
      md: "size-2",
      lg: "size-2.5",
      xl: "size-3",
    },
    ring: {
      true: "ring-[3px] ring-offset-[2px] ring-offset-background",
      false: "",
    },
    pulse: {
      true: "animate-status-pulse",
      false: "",
    },
  },
  compoundVariants: [
    { ring: true, tone: "red", class: "ring-[oklch(0.62_0.22_25_/_0.20)]" },
    { ring: true, tone: "blocked", class: "ring-[oklch(0.62_0.22_25_/_0.20)]" },
    { ring: true, tone: "amber", class: "ring-[oklch(0.74_0.16_80_/_0.22)]" },
    { ring: true, tone: "active", class: "ring-[oklch(0.74_0.16_80_/_0.22)]" },
    { ring: true, tone: "green", class: "ring-[oklch(0.66_0.16_155_/_0.20)]" },
    { ring: true, tone: "resolved", class: "ring-[oklch(0.66_0.16_155_/_0.20)]" },
    { ring: true, tone: "cyan", class: "ring-[oklch(0.72_0.17_190_/_0.22)]" },
    { ring: true, tone: "purple", class: "ring-[oklch(0.55_0.2_300_/_0.22)]" },
  ],
  defaultVariants: { tone: "open", size: "md", ring: false, pulse: false },
});

/** The set of tones a StatusDot can render — derived from the cva so it never drifts. */
export type StatusDotTone = NonNullable<VariantProps<typeof statusDot>["tone"]>;

interface StatusDotProps extends VariantProps<typeof statusDot> {
  className?: string;
}

export function StatusDot({ tone, size, ring, pulse, className }: StatusDotProps) {
  return (
    <span
      data-slot="status-dot"
      className={cn(statusDot({ tone, size, ring, pulse }), className)}
    />
  );
}

export function statusToTone(status: Status): NonNullable<VariantProps<typeof statusDot>["tone"]> {
  return status;
}

export function ragToTone(rag: Rag): NonNullable<VariantProps<typeof statusDot>["tone"]> {
  return rag;
}

export const STATUS_LABEL: Record<Status, string> = {
  open: "Open",
  active: "Active",
  resolved: "Resolved",
  blocked: "Blocked",
};

export const TASK_STATUS_LABEL: Record<TaskStatus, string> = {
  todo: "To do",
  in_progress: "In progress",
  done: "Done",
  blocked: "Blocked",
};

const TASK_STATUS_TONE: Record<TaskStatus, NonNullable<VariantProps<typeof statusDot>["tone"]>> = {
  todo: "open",
  in_progress: "active",
  done: "resolved",
  blocked: "blocked",
};

export function taskStatusToTone(
  status: TaskStatus
): NonNullable<VariantProps<typeof statusDot>["tone"]> {
  return TASK_STATUS_TONE[status];
}

export const PRIORITY_LABEL: Record<Priority, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
};

const PRIORITY_TONE: Record<Priority, NonNullable<VariantProps<typeof statusDot>["tone"]>> = {
  low: "green",
  medium: "amber",
  high: "red",
};

export function priorityToTone(
  priority: Priority
): NonNullable<VariantProps<typeof statusDot>["tone"]> {
  return PRIORITY_TONE[priority];
}
